import { isAfter, isWithinInterval } from '@if/core/date'
import { ECreditApplicationStatus, EDecision, EFinancingProgram, EWorksheetStatus } from '@if/core/types'
import { formatCurrency, formatPercentage } from '@if/core/utils'
import * as yup from '@if/core/yup'
import { Action, ActionList, Banner, Caption, ControlledCheckbox, useCan, useDialog, useFormDialog } from '@if/mui'
import { Box } from '@mui/material'
import { access } from '@src/access'
import GetCreditContentConsentText from '@src/containers/CreditApplicationPage/components/creditApplicationForm-selectors'
import { FinancingConfigDto } from '@src/data/api/configs-api/financing-config-api'
import { useQualify } from '@src/data/api/credit-api/credit-api'
import { useApplicablePromoPlanLowestRate, useResolvedPaymentPlan } from '@src/data/api/merchants-api/merchants-api'
import {
  anyDocsAwaitingApproval,
  getDisplayedMaxAmountFinanced,
  mustShowInterestRate,
} from '@src/data/creditapp-selectors'
import { FinancingProgramConfigs } from '@src/types/Constants'
import { CreditApplication } from '@src/types/CreditApplicationSchema'
import { Merchant } from '@src/types/Merchant'
import { Calculator, Send, UserPlus } from 'lucide-react'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { hasPendingAsyncChecks, hasPendingCreditChecks } from '../../credit-hooks'
import { ECreditApplicationStatusMessage, GetStatusFromDecision } from '../../viewCreditApp-selectors'
import ApplicationError from '../applicationError'
import CreditApplicantBanners from '../CreditSection/CreditApplicantBanners'
import PaymentPlanSimulator from '../CreditSection/PaymentPlanSimulator'
import { getCreditTilePlan } from './creditTile'
import StageContent, { StageField } from './StageContent'

interface Props {
  creditApp: CreditApplication
  merchant: Merchant
  financingConfig: FinancingConfigDto
  canEdit: boolean
}

const consentSchema = yup.object({
  consent: yup.boolean().oneOf([true]).required(),
})

const creditStatus = (creditApp: CreditApplication) => {
  // Pending checks count as "analysing" here, so the step doesn't report an error while its
  // own banner says the checks are still running.
  return GetStatusFromDecision(
    creditApp.finalCreditDecision.decision,
    false,
    creditApp,
    hasPendingCreditChecks(creditApp),
    anyDocsAwaitingApproval(creditApp.requiredDocuments),
  )
}

// Credit descriptor for StageContent: granted amounts + per-applicant banners + actions.
const CreditStageContent = ({ creditApp, merchant, financingConfig, canEdit }: Props) => {
  const { t } = useTranslation()
  const showRate = useCan(access.showRate)
  const [qualify] = useQualify()
  const simulatorDialog = useDialog({
    title: t('viewCreditApplication.paymentPlanSimulator'),
    maxWidth: 'md',
    fullWidth: true,
    hideConfirm: true,
    cancelLabel: t('common.close'),
    content: (ctx) => <PaymentPlanSimulator creditApp={creditApp} merchant={merchant} onApplied={ctx.close} />,
  })
  const consentDialog = useFormDialog<{ consent: boolean }, void, void>({
    title: t('submission.submission'),
    confirmLabel: t('submission.submit'),
    schema: consentSchema,
    defaultValues: { consent: false },
    successMessage: false,
    content: (ctx) => (
      <>
        <GetCreditContentConsentText financingProgram={financingProgramId} />
        <ControlledCheckbox control={ctx.form.control} name="consent" label={t('submission.agreeToTerms')} />
      </>
    ),
    onSubmit: async () => {
      await qualify({
        creditApplicationId: id,
        financingProgramId,
        prequalPromoCode: creditApp.prequalPromoCode ?? undefined,
      })
    },
  })

  const { finalCreditDecision, worksheet, financingProgramId, id, coApplicant, consentHardHit } = creditApp
  const decision = finalCreditDecision.decision
  const config = FinancingProgramConfigs[financingProgramId]
  // Any check still owes a result (banner) vs. this step's numbers aren't computed yet (skeleton).
  const pendingChecks = hasPendingAsyncChecks(creditApp)
  const status = creditStatus(creditApp)
  const mustDisplayInterests = mustShowInterestRate(creditApp, merchant, showRate)

  const hasActivePaymentPlans = useMemo(() => {
    if (!merchant?.paymentPlans) return false
    const now = new Date()
    const distantFuture = new Date()
    distantFuture.setFullYear(distantFuture.getFullYear() + 100)
    return merchant.paymentPlans.some((plan) => {
      const from = new Date(plan.activeFrom)
      const to = new Date(plan?.expiresOn ?? distantFuture)
      return isWithinInterval(now, { start: from, end: to }) && isAfter(to, now)
    })
  }, [merchant?.paymentPlans])

  const appliedPaymentPlanId = worksheet?.paymentPlanId ?? creditApp.merchantPaymentPlanId ?? undefined
  const [appliedPlan, isFetchingAppliedPlan] = useResolvedPaymentPlan(merchant?.id, appliedPaymentPlanId)

  const isCustomBuydownActive = useCan(access.customBuydownActive, { merchant })
  const [lowestBuydownTermRate, isFetchingLowestBuydownRate] = useApplicablePromoPlanLowestRate(
    isCustomBuydownActive ? merchant?.id : undefined,
  )
  const isComputingCredit =
    (consentHardHit && creditApp.normsCreditDecision == null) ||
    isFetchingAppliedPlan ||
    (isCustomBuydownActive && isFetchingLowestBuydownRate)

  const tilePlan = getCreditTilePlan({
    appliedPlan,
    worksheetTerm: worksheet?.term ?? null,
    lowestAvailableRate: lowestBuydownTermRate,
    decisionMaxTerm: finalCreditDecision.maxTermDuration,
  })
  const { promoRate } = tilePlan
  const promoRateText =
    promoRate &&
    `${formatPercentage(promoRate.promoRate * 100)}% (${t('worksheet.termMonths', { count: promoRate.durationMonths })})`

  // Granted amounts — shown once the credit is approved (each row gated by config).
  const fields: StageField[] =
    decision !== EDecision.Approved
      ? []
      : [
          ...(config.useMaxPayment
            ? [
                {
                  label: t('viewCreditApplication.maxMonthlyPayment'),
                  value: formatCurrency(finalCreditDecision.maxPmtAmount),
                },
              ]
            : []),
          ...(config.displayAmountGrantedCreditTile
            ? [
                {
                  label: t('viewCreditApplication.amountGranted'),
                  value: formatCurrency(
                    getDisplayedMaxAmountFinanced(
                      financingProgramId,
                      finalCreditDecision.maxAmountFinanced,
                      financingConfig,
                    ),
                  ),
                },
              ]
            : []),
          {
            label: t('viewCreditApplication.maxTerm'),
            value: creditApp.isSinglePayment
              ? t('viewCreditApplication.isSinglePayment')
              : tilePlan.maxTermMonths != null
                ? t('worksheet.termMonths', { count: tilePlan.maxTermMonths })
                : undefined,
          },
          ...(mustDisplayInterests
            ? [
                {
                  label: t('viewCreditApplication.interestRate'),
                  value: `${finalCreditDecision.interestRate}%`,
                },
                ...(promoRate != null
                  ? [
                      {
                        label: `${t('viewCreditApplication.interestRate')} (${t('common.promoPlan')})`,
                        value: promoRate.isLowestAvailable ? (
                          <>
                            {`${promoRateText}*`}
                            <Caption sx={{ display: 'block' }}>
                              {t('viewCreditApplication.longerTermFinancingNote')}
                            </Caption>
                          </>
                        ) : (
                          promoRateText
                        ),
                      },
                    ]
                  : []),
              ]
            : []),
          { label: t('worksheet.paymentPlan'), value: tilePlan.planLabel ?? t('worksheet.regularPlan') },
        ]

  // App-level "previous application" warning (Products gets the conditional-approval variant).
  const previousAppWarningKey =
    config.requiresCreditStepIntervention && creditApp.applicant.creditAlerts?.hasPreviousApplication
      ? financingProgramId === EFinancingProgram.Products &&
        creditApp.normsCreditDecision?.decision === EDecision.Approved
        ? ('viewCreditApplication.previousAppConditionalApproval' as const)
        : ('viewCreditApplication.previousApp' as const)
      : undefined

  // Per-applicant OK / alert banners + the state's message and interactive tools.
  const renderCustomContent = () => {
    // Read-only app still awaiting the client's worksheet/approval (declines fall through to the per-applicant banners).
    if (
      creditApp.worksheet === null &&
      creditApp.consentHardHit === true &&
      !canEdit &&
      config.worksheetIsNeededForCredit &&
      decision !== EDecision.Refused
    ) {
      return (
        <>
          <Banner severity="info">{t('viewCreditApplication.awaitingClientApproval')}</Banner>
          {simulatorButton}
        </>
      )
    }
    if (status === ECreditApplicationStatusMessage.Error) return <ApplicationError />
    if (!consentHardHit && creditApp.status === ECreditApplicationStatus.Active) {
      return canEdit ? (
        <ActionList actions={[qualifyAction]} variant="button" menuLabel={t('viewCreditApplication.actions')} />
      ) : (
        <Banner severity="info">{t('viewCreditApplication.awaitingClientConsent')}</Banner>
      )
    }
    return (
      <>
        {previousAppWarningKey && <Banner severity="warning">{t(previousAppWarningKey)}</Banner>}
        <CreditApplicantBanners
          applicant={creditApp.applicant}
          coApplicant={coApplicant}
          status={status}
          showAlerts={config.requiresCreditStepIntervention}
        />
        {simulatorButton}
      </>
    )
  }

  const changeCoapp: Action = {
    icon: UserPlus,
    label: coApplicant ? t('viewCreditApplication.changeCoapp') : t('viewCreditApplication.addCoapp'),
    href: `/credits/${financingProgramId}/${id}/edit?addCoApp=true#coapplicant`,
    disabled: finalCreditDecision.isHardDecline,
  }
  const qualifyAction: Action = {
    icon: Send,
    label: t('viewCreditApplication.qualify'),
    onClick: () => void consentDialog.create(),
  }
  const isWorksheetSaved = worksheet != null && worksheet.status !== EWorksheetStatus.Draft
  const openSimulatorAction: Action = {
    icon: Calculator,
    label: t('viewCreditApplication.paymentPlanSimulator'),
    disabled: isWorksheetSaved,
    tooltip: isWorksheetSaved ? t('viewCreditApplication.simulatorWorksheetSaved') : undefined,
    onClick: () => void simulatorDialog.open(),
  }
  const simulatorButton = decision === EDecision.Approved &&
    hasActivePaymentPlans &&
    financingProgramId === EFinancingProgram.Personal && (
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <ActionList actions={[openSimulatorAction]} variant="icon" menuLabel={t('viewCreditApplication.actions')} />
      </Box>
    )

  const buildActions = (): Action[] => {
    if (!canEdit) return []
    if (status === ECreditApplicationStatusMessage.Declined && config.allowCoappAfterDecline) return [changeCoapp]
    return []
  }

  const actions = buildActions()

  return (
    <StageContent
      fields={fields}
      columns={3}
      loading={isComputingCredit}
      pendingChecks={pendingChecks}
      customContent={renderCustomContent()}
      actions={
        actions.length > 0 ? (
          <ActionList actions={actions} variant="button" menuLabel={t('viewCreditApplication.actions')} />
        ) : undefined
      }
    />
  )
}

export default CreditStageContent
